import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Divider, Flex } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Bleed, Main, Padding, SidePanel } from '../../common/components';
import {
  confirmPanelFormClose,
  Form,
  FormCheckbox,
  FormDateRangePicker,
  FormField,
  FormInput,
  FormRadioGroup,
  FormSelect,
  FormSubmitButton,
} from '../../common/form';
import { SystemClassifierFormSelect } from '../../configuration/SystemClassifierFormSelect';
import { RequiredKeyDataType } from '../schema';
import {
  RequiredKeyDataSchema,
  RequiredKeyStatusDataSchema,
} from '../schema/RequiredKeyDataSchema';
import translations from '../validation/KeyEditPanel.translations.json';
import './KeyEditPanel.css';
import { useKeyClassifiers } from './useKeyClassifiers';

const { addTranslations, useTranslation } = i18nNsFactory('key-edit-panel');
addTranslations(translations);

export interface KeyEditPanelProps {
  onClose: () => void;
  onSubmit: (key: RequiredKeyDataType, clone?: boolean) => void;
  index?: number;
  indicatorStatus?: boolean;
  clone?: boolean;
  requiredKey: RequiredKeyDataType;
}

export function KeyEditPanel({
  onClose,
  onSubmit,
  index,
  indicatorStatus,
  clone,
  requiredKey,
}: KeyEditPanelProps) {
  const recipientIds = useWatch({ name: 'recipientIds' });
  const handleSubmit = useCallback(
    (formData: RequiredKeyDataType) => {
      onSubmit(formData, clone);
    },
    [onSubmit, clone],
  );

  return (
    <Form
      schema={
        indicatorStatus ? RequiredKeyStatusDataSchema : RequiredKeyDataSchema
      }
      defaultValues={requiredKey}
      onSubmit={handleSubmit}
      idAttributePrefix={`requiredKey-${
        typeof index === 'undefined' ? 'create' : index
      }`}
    >
      <KeyEditForm
        requiredKey={requiredKey}
        onClose={onClose}
        recipientIds={recipientIds}
        indicatorStatus={indicatorStatus}
      />
    </Form>
  );
}

export const KeyEditForm = ({
  onClose,
  recipientIds,
  requiredKey,
  indicatorStatus,
}: Pick<KeyEditPanelProps, 'requiredKey' | 'onClose' | 'indicatorStatus'> & {
  recipientIds?: number[];
}) => {
  const { t } = useTranslation();
  const { isDirty } = useFormState();

  const keyRecipientIds = useWatch({ name: 'recipientIds' });

  const allRecipientIds = useMemo(
    () => Array.from(new Set([...(recipientIds || []), ...keyRecipientIds])),
    [recipientIds, keyRecipientIds],
  );

  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(() => onClose());
    } else {
      onClose();
    }
  }, [onClose, isDirty]);

  return (
    <SidePanel
      minWidth={700}
      side="right"
      collapsedWidth={700}
      width={1786}
      mode="modal"
    >
      <SidePanelWrapper>
        <Padding>
          <Flex align="end" justify="space-between">
            <h4>
              {t(requiredKey.id ? 'key.heading.label' : 'key.heading.new')}
            </h4>
          </Flex>
        </Padding>
        <Divider style={{ margin: 0 }} />
        <FormWrapper vertical>
          <Scrollable>
            <Padding>
              <KeyEditFormInner
                indicatorStatus={indicatorStatus}
                recipientIds={allRecipientIds}
              />
            </Padding>
          </Scrollable>
        </FormWrapper>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button id="cancel-key-edit" onClick={handleCancel}>
              {t('button.cancel')}
            </Button>
            <FormSubmitButton>{t('button.ok')}</FormSubmitButton>
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
};

function KeyEditFormInner({
  indicatorStatus,
  recipientIds,
}: {
  indicatorStatus?: boolean;
  recipientIds: number[];
}) {
  const { t } = useTranslation();
  const { recipientClassifiers, statusClassifiers, ruleClassifiers } =
    useKeyClassifiers();
  const recipientOptions = useMemo(
    () =>
      recipientClassifiers.filter((option) =>
        recipientIds?.includes(Number(option.value)),
      ),
    [recipientIds, recipientClassifiers],
  );

  const { setValue } = useFormContext<RequiredKeyDataType>();
  const ruleTypeId = useWatch({ name: 'ruleTypeId' });
  const statusId = useWatch({ name: 'statusId' });
  const statusCode = statusClassifiers.find(
    (status) => Number(status.value) === statusId,
  )?.code;
  const isEmptyValueRuleAllowed =
    !indicatorStatus || statusCode === 'L' || statusCode === 'M';

  useEffect(() => {
    if (!ruleTypeId) return;

    const ruleCode = ruleClassifiers.find(
      (r) => Number(r.value) === Number(ruleTypeId),
    )?.code;
    if (!ruleCode) return;

    // Clear ruleTypeId if it's selected and not allowed
    if (ruleCode === 'EMPTY_VALUE' && !isEmptyValueRuleAllowed) {
      setValue('ruleTypeId', null);
    }
  }, [isEmptyValueRuleAllowed, ruleClassifiers, ruleTypeId, setValue]);

  const handleRuleTypeFilter = useCallback(
    (option: { code: string }) =>
      option.code === 'EMPTY_VALUE' ? isEmptyValueRuleAllowed : true,
    [isEmptyValueRuleAllowed],
  );

  return (
    <Flex vertical id="key-edit-wrapper">
      <div className="key-section">
        <FormField
          className="cols-2"
          name="key"
          label={t('fields.key.label')}
          required
        />
        <FormField
          name="name"
          label={t('fields.name.label')}
          className="cols-2"
        />
        {indicatorStatus && (
          <FormField
            className="cols-2"
            name="statusId"
            label={t('fields.statusId.label')}
            required
          >
            <FormRadioGroup name="statusId" options={statusClassifiers} />
          </FormField>
        )}
        <FormField
          className="cols-2"
          name="ruleTypeId"
          label={t('fields.ruleTypeId.label')}
        >
          <SystemClassifierFormSelect
            allowClear
            code={
              indicatorStatus ? 'IND_STATUS_RULE_TYPE' : 'IND_VALUE_RULE_TYPE'
            }
            name="ruleTypeId"
            filterFn={handleRuleTypeFilter}
          />
        </FormField>
        <FormField
          className="cols-2"
          name="validPeriod"
          label={t('fields.validPeriod.label')}
        >
          <FormDateRangePicker name="validPeriod" allowEmpty={[true, true]} />
        </FormField>
      </div>
      <Bleed direction="horizontal">
        <Divider />
      </Bleed>
      <div className="key-section">
        <FormField
          className="cols-2"
          name="voluntary"
          label={t('fields.voluntary.label')}
          style={{ flexGrow: 1 }}
        >
          <FormCheckbox name="voluntary" />
        </FormField>
        <FormField
          name="recipientIds"
          label={t('fields.recipientIds.label')}
          className="cols-2"
          required
        >
          <FormSelect
            name="recipientIds"
            mode="multiple"
            options={recipientOptions}
          />
        </FormField>
        <FormField
          className="cols-2"
          name="description"
          label={t('fields.description.label')}
          style={{ flexGrow: 1 }}
        >
          <FormInput
            className="validation-text"
            name="description"
            maxLength={2000}
            rows={2}
          />
        </FormField>
      </div>
    </Flex>
  );
}

const FormWrapper = styled(Flex)`
  overflow-y: hidden;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const Scrollable = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
`;

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;
